import React, { useEffect, useRef, useState } from "react";
import Control from "../../common/Control";
import ModaleLocale from "../../common/CustomModal/ModaleLocale";
import CartesRolesCompte from "./CartesRolesCompte";
import CaseAlertesPlanning from "./CaseAlertesPlanning";
import ChoixSitesCompte from "./ChoixSitesCompte";
import DroitsProfil from "./DroitsProfil";
import { creerCompteOperateur } from "./operateursApi";
import { FORMULAIRE_COMPTE_VIDE, corpsCreation, rolesProposes, validerFormulaireCompte } from "./comptesOperateur";

// ============================================================
// Création d'un compte d'opérateur par la Caf (CE-1407, maquette 10) :
// identité, rôle dans la structure avec l'encadré des droits, option
// « alertes de planning », sites de la structure, date de fin de contrat.
// Pas de mot de passe : l'e-mail d'activation invite le titulaire à choisir
// le sien. La modification se fait sur la fiche du compte, pas ici.
// Erreurs 400/419 sous les champs, refus 409 en bandeau.
// ============================================================

const CIVILITES = [
  { id: 1, name: "Monsieur" },
  { id: 2, name: "Madame" },
];

const formulaireInitial = (sites) => ({
  ...FORMULAIRE_COMPTE_VIDE,
  // Un seul site : il est coché d'office (maquette 10).
  sites: sites.length === 1 ? [sites[0].id] : [],
});

const ModaleCompteOperateur = ({ ouvert, onFermer, operateur, sites = [], onEnregistre }) => {
  const [formulaire, setFormulaire] = useState(() => formulaireInitial(sites));
  const [erreurs, setErreurs] = useState({});
  const [refus, setRefus] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const ouvertRef = useRef(ouvert);

  useEffect(() => {
    ouvertRef.current = ouvert;
    if (ouvert) {
      setFormulaire(formulaireInitial(sites));
      setErreurs({});
      setRefus(null);
      setEnregistrement(false);
    }
    // Les sites ne réinitialisent pas une saisie en cours : seule l'ouverture le fait.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert]);

  const modifierChamp = (name, valeur) => {
    setFormulaire((f) => ({ ...f, [name]: valeur }));
    setErreurs((er) => ({ ...er, [name]: undefined }));
    setRefus(null);
  };

  const changer = (e) => {
    const { name, value, type, checked } = e.target;
    modifierChamp(name, type === "checkbox" ? checked : value);
  };

  const soumettre = (e) => {
    e.preventDefault();
    const invalides = validerFormulaireCompte(formulaire, { nombreSites: sites.length });
    if (Object.keys(invalides).length) {
      setErreurs(invalides);
      return;
    }
    const corps = corpsCreation(formulaire);
    setEnregistrement(true);
    setErreurs({});
    setRefus(null);
    creerCompteOperateur(operateur.id, corps).then(
      (fiche) => {
        if (!ouvertRef.current) return;
        setEnregistrement(false);
        onEnregistre(fiche, corps.email);
      },
      (erreur) => {
        if (!ouvertRef.current) return;
        setEnregistrement(false);
        if (erreur.champs) {
          setErreurs(erreur.champs);
          const horsModale = Object.keys(erreur.champs).filter((c) => !(c in FORMULAIRE_COMPTE_VIDE));
          if (horsModale.length) setRefus(horsModale.map((c) => erreur.champs[c]).join(" "));
        } else {
          setRefus(erreur.message || "Une erreur est survenue");
        }
      }
    );
  };

  return (
    <ModaleLocale
      ouvert={ouvert}
      onFermer={onFermer}
      titre={`Ajouter un utilisateur à ${operateur.nom}`}
      sousTitre={`Le compte est rattaché à ${operateur.nom} et à ses seuls sites.`}
      taille="moyenne"
      idTitre="modale-compte-operateur"
      peutFermer={!enregistrement}
      pied={
        <>
          <p className="modale-operateur__aide-pied">
            Un e-mail d'activation est envoyé : l'utilisateur choisit son mot de passe en activant son compte.
          </p>
          <button type="button" className="btn btn-ghost" onClick={onFermer} disabled={enregistrement}>
            Annuler
          </button>
          <button type="submit" form="formulaire-compte-operateur" className="btn btn-primary" disabled={enregistrement}>
            <i className="fa fa-paper-plane mr-2" aria-hidden="true" />
            Créer et envoyer l'invitation
          </button>
        </>
      }
    >
      {ouvert && (
        <form id="formulaire-compte-operateur" onSubmit={soumettre} noValidate>
          {refus && (
            <div className="alert alert-danger" role="alert">
              {refus}
            </div>
          )}

          <div className="modale-operateur__champs">
            <Control
              margin="my-0"
              label="Civilité"
              type="select"
              name="civilite"
              value={formulaire.civilite}
              datas={CIVILITES}
              dataIndex="id"
              dataLabel="name"
              noSelectFirstOption
              change={changer}
              error={erreurs}
            />
            <div className="modale-operateur__vide" aria-hidden="true" />
            <Control
              margin="my-0"
              label="Prénom"
              name="prenom"
              value={formulaire.prenom}
              change={changer}
              error={erreurs}
              required
              inputProps={{ autoComplete: "off" }}
            />
            <Control
              margin="my-0"
              label="Nom"
              name="nom"
              value={formulaire.nom}
              change={changer}
              error={erreurs}
              required
              inputProps={{ autoComplete: "off" }}
            />
            <div>
              <Control
                margin="my-0"
                label="E-mail professionnel"
                type="email"
                name="email"
                value={formulaire.email}
                change={changer}
                error={erreurs}
                required
                inputProps={{ autoComplete: "off", inputMode: "email" }}
              />
              <p className="modale-operateur__aide">
                L'e-mail d'activation y est envoyé. Une adresse ne sert qu'à un seul compte ESPOAR.
              </p>
            </div>
            <Control
              margin="my-0"
              label="Téléphone"
              type="tel"
              name="telephone"
              value={formulaire.telephone}
              change={changer}
              error={erreurs}
              inputProps={{ inputMode: "tel" }}
            />
          </div>

          <CartesRolesCompte
            roles={rolesProposes({ editeur: null })}
            valeur={formulaire.role}
            onChange={(role) => modifierChamp("role", role)}
            erreur={erreurs.role}
          />
          <div className="modale-operateur__alertes">
            <CaseAlertesPlanning
              coche={formulaire.recoitAlertesPlanning}
              onChange={(valeur) => modifierChamp("recoitAlertesPlanning", valeur)}
            />
          </div>
          <DroitsProfil regimes={operateur.regimes} role={formulaire.role} nomStructure={operateur.nom} />

          <div className="modale-operateur__section">
            <ChoixSitesCompte
              sites={sites}
              selection={formulaire.sites}
              onChange={(ids) => modifierChamp("sites", ids)}
              nomStructure={operateur.nom}
              erreur={erreurs.sites}
              idPrefix="creation-compte"
            />
          </div>

          <div className="modale-operateur__section modale-operateur__date">
            <Control
              margin="my-0"
              label="Date de fin de contrat (facultatif)"
              type="date"
              name="dateFinContrat"
              value={formulaire.dateFinContrat}
              change={changer}
              error={erreurs}
            />
            <p className="modale-operateur__aide">
              Pour un contrat court : le compte ne peut plus se connecter après cette date.
            </p>
          </div>
        </form>
      )}
    </ModaleLocale>
  );
};

export default ModaleCompteOperateur;
